import ErrorNote from './ErrorNote'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'

import { fetchStaffCandidates, makeDancerStaff } from '../api/staffAdmin'
import { useCan } from '../useSession'
import { Award, Cake, CalendarDays, Home, Mail, Phone, Ruler } from 'lucide-react'
import {
  fetchDancer,
  measurementAge,
  measurementFields,
  recordedMeasurements,
  saveMeasurements,
  setAccountCredit,
  setCompanyMember,
  setDancerTeam,
  MINI_TEAM,
  tenureLabel,
  type DancerMeasurements,
  type DancerRecord,
  type MeasurementKey,
} from '../api/dancerAdmin'
import { fetchAttendancePanel, fetchBankedFor } from '../api/attendance'
import { formatFullDate } from '../api/calendar'
import AttendancePanel from './AttendancePanel'
import BankedInHand from './BankedInHand'
import BackLink from './BackLink'

/**
 * Everything the studio holds about one dancer (D-151).
 *
 * Ordered by how often it is asked for, not by how the data is stored: who she
 * is, how long she has been here, what she is in this week, and what size she
 * is. The family sits underneath — it is how billing is organised, not what
 * anyone came to this screen for.
 */
export default function DancerRecordView() {
  const { id = '' } = useParams()

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['dancer', id],
    queryFn: () => fetchDancer(id),
    enabled: Boolean(id),
  })

  /*
   * Its own query on purpose. Standing needs every occurrence of every class she
   * takes, which is a far heavier read than the record itself — and most visits
   * to this page are to check a costume size or a phone number. Loading it
   * alongside means the page renders immediately and the badges fill in (D-169).
   */
  const { data: panel } = useQuery({
    queryKey: ['dancer-panel', id],
    queryFn: () => fetchAttendancePanel(id),
    enabled: Boolean(id),
  })

  /*
   * <b>What she is holding, on the studio’s own record of her</b> (D-764).
   *
   * `asked`: *"on tonya's view she should be able to see how many are banked
   * when looking at a student."* The family gained this panel the same evening
   * (D-757) and the studio had only the roster-wide list (D-745) - so the one
   * screen about **this** dancer was the one that did not say.
   *
   * The same route the family reads, which the scope resolver already answers
   * for anybody holding `ViewAllSchedules`: one count, three readers, and no
   * third way for the number to be worked out (A-17).
   */
  const { data: banked } = useQuery({
    queryKey: ['dancer-banked', id],
    queryFn: () => fetchBankedFor(id),
    enabled: Boolean(id),
  })

  if (isPending) return <Shell>Loading...</Shell>

  if (isError) {
    return (
      <Shell>
        <ErrorNote boxed>
          {(error as Error).message}
        </ErrorNote>
      </Shell>
    )
  }

  return (
    <div className="page-shell px-5 py-8">
      {/*
        This said "All dancers" however you arrived (D-353). The record is
        reached from the dancer list, from a search hit, and from a `Dancer`
        notification — so for two of its three doors the link named a screen
        she had never opened. `/admin/dancers` stays as the fallback because it
        needs the same `ViewPeople` this record does, so it can never refuse
        somebody who got here.
      */}
      <BackLink to="/admin/dancers" label="All dancers" />

      <header className="mb-5">
        {/* `wrap-anywhere` stays: a heading that is somebody's name has to
            break, and this one is the widest thing on a phone (D-115). The
            size and family are the house heading (D-335). */}
        <h1 className="font-display text-2xl wrap-anywhere">
          {data.firstName} {data.lastName}
        </h1>
        <p className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[13px] text-ink-3">
          <span className="inline-flex items-center gap-1.5">
            <Cake size={13} strokeWidth={2.5} />
            <span className="tabular">{data.age}</span> &middot;{' '}
            {formatFullDate(data.dateOfBirth)}
          </span>
          {data.status !== 'Active' && (
            <span className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11.5px] font-semibold">
              {data.status}
              {data.withdrawnOn && ` ${formatFullDate(data.withdrawnOn)}`}
            </span>
          )}
        </p>
      </header>

      <div className="flex flex-col gap-4">
        {/*
          Tenure leads because it is the thing that is genuinely hard to find
          anywhere else — Jackrabbit holds the start date but never the answer,
          and Tonya currently works it out by memory at recital time.
        */}
        <section
          className={`rounded-2xl border px-5 py-4 ${
            data.milestone !== null ? 'border-good bg-good-bg' : 'border-line bg-surface'
          }`}
        >
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <Award
              size={17}
              strokeWidth={2.5}
              className={`self-center ${data.milestone !== null ? 'text-good' : 'text-ink-3'}`}
            />
            <p className="text-[17px] font-bold">{tenureLabel(data.years)}</p>
            {data.startedOn && (
              <p className="text-[12.5px] text-ink-3">
                since {formatFullDate(data.startedOn)}
              </p>
            )}
          </div>

          <p className="mt-1 pl-[29px] text-[13px] text-ink-2">
            {data.milestone !== null ? (
              <strong>Due a {data.milestone}-year award this season.</strong>
            ) : data.years === null ? (
              // The honest version. Zero would read as "her first season".
              'No start date in Jackrabbit, so tenure cannot be worked out. Adding one there brings it across on the next import.'
            ) : data.nextMilestone !== null ? (
              <>
                Next award at {data.nextMilestone} years
                {data.yearsToNextMilestone !== null && (
                  <>
                    {' — '}
                    {data.yearsToNextMilestone === 1
                      ? 'one more season'
                      : `${data.yearsToNextMilestone} more seasons`}
                  </>
                )}
                .
              </>
            ) : (
              'Past every award year on the list.'
            )}
          </p>
        </section>

        {/*
          The same panel the family sees (D-589), as facts: the standing with
          its verdicts, and the nights she missed under the class each belongs
          to, with what is repairing each. It replaced a badge in the class
          list's header and a badge per class row, which were two more
          colourings of the same figures on one screen.
        */}
        {panel?.standing && (
          <AttendancePanel standing={panel.standing} missed={panel.missed ?? []} viewer="staff" />
        )}

        <BankedInHand banked={banked} />

        <section className="overflow-hidden rounded-2xl border border-line bg-surface">
          <header className="flex items-center gap-2.5 border-b border-line bg-surface-2 px-4 py-3">
            <CalendarDays size={15} strokeWidth={2.5} className="shrink-0 text-ink-2" />
            <h2 className="flex-1 text-[13.5px] font-bold">
              Classes
              <span className="tabular ml-2 font-semibold text-ink-3">{data.classes.length}</span>
            </h2>
          </header>

          {data.classes.length === 0 ? (
            <p className="px-4 py-4 text-[13px] text-ink-3">Not enrolled in anything.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {data.classes.map((c) => (
                <li key={c.classId} className="flex items-start gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                  <p className="text-[13.5px] font-semibold wrap-anywhere">{c.name}</p>
                  <p className="mt-0.5 text-[12.5px] text-ink-3">
                    {/* No start time means the class meets on no dates (D-84). */}
                    {c.time ? (
                      <>
                        {c.day} {c.time}
                      </>
                    ) : (
                      <span className="italic">No time set yet</span>
                    )}
                    {c.room && <> &middot; {c.room}</>}
                    {c.teachers && <> &middot; {c.teachers}</>}
                    {c.status !== 'Active' && <> &middot; {c.status}</>}
                  </p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <MeasurementsCard dancerId={data.id} record={data} />

        <section className="overflow-hidden rounded-2xl border border-line bg-surface">
          <header className="flex items-center gap-2.5 border-b border-line bg-surface-2 px-4 py-3">
            <Home size={15} strokeWidth={2.5} className="shrink-0 text-ink-2" />
            <h2 className="flex-1 text-[13.5px] font-bold">{data.householdName} family</h2>
            {/*
              <b>A finger-sized target that takes up no more room</b> (D-516).
              It measured 69&times;19 at every width — the first thing found on
              this screen once the sweep could reach it at all, since the record
              needs an id and nothing had ever driven it.

              The padding grows the hit area and the matching negative margin
              takes the space back, so the header keeps the height it had. Plain
              padding would have pushed this row ~11px taller, which is a
              visible change made to fix an invisible one.

              Adapted from `CoverActions`, not copied: those links carry
              `-mx-1 px-1 py-1.5` and deliberately *do* grow their row, because
              each is a block control in a column. This one sits in a flex
              header beside the household name, so the vertical half has to be
              given back too.
            */}
            <Link
              to={`/admin/people?household=${data.householdId}`}
              className="-mx-1 -my-1.5 px-1 py-1.5 text-[12.5px] font-semibold text-accent hover:underline"
            >
              Open family
            </Link>
          </header>

          {data.guardians.length === 0 ? (
            <p className="px-4 py-4 text-[13px] text-ink-3">No contacts on file.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {data.guardians.map((g, i) => (
                <li key={`${g.name}-${i}`} className="px-4 py-3">
                  <p className="text-[13.5px] font-semibold">{g.name}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-ink-3">
                    {g.email && (
                      <span className="inline-flex items-center gap-1.5 wrap-anywhere">
                        <Mail size={12} strokeWidth={2.5} />
                        {g.email}
                      </span>
                    )}
                    {g.phone && (
                      <span className="inline-flex items-center gap-1.5">
                        <Phone size={12} strokeWidth={2.5} />
                        {g.phone}
                      </span>
                    )}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        {data.notes && (
          <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
            <h2 className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
              Notes
            </h2>
            <p className="mt-1 text-[13.5px] wrap-anywhere text-ink-2">{data.notes}</p>
          </section>
        )}

        <CompanyCard
          dancerId={id}
          firstName={data.firstName}
          isCompanyMember={data.isCompanyMember ?? false}
          canSet={data.canSetCompanyMember ?? false}
        />

        <MiniTeamCard
          dancerId={id}
          firstName={data.firstName}
          teams={data.teams ?? []}
          canSet={data.canSetCompanyMember ?? false}
        />

        <AccountCreditCard
          dancerId={id}
          firstName={data.firstName}
          holdsAccountCredit={data.holdsAccountCredit ?? false}
          canSet={data.canSetCompanyMember ?? false}
        />

        <StaffRecordCard
          dancerId={id}
          firstName={data.firstName}
          lastName={data.lastName}
          alreadyIs={data.staffRecordName}
        />
      </div>
    </div>
  )
}

/**
 * Whether she is on the company team (D-652).
 *
 * **Owner, from the pilot:** *"An elite group needing 100% attendance, with four
 * weeks to make up a missed class. Probably a flag on the student account
 * marking them company."*
 *
 * **It says what it does, not what it is.** *Company* alone is a label somebody
 * has to already know the meaning of; the consequence — a higher bar, and told
 * where she stands straight away — is the part that makes ticking it a
 * decision rather than a filing exercise.
 *
 * **Shown to everybody who can read the record, settable only by whoever may
 * edit it.** A teacher seeing the flag is how she understands why a dancer's
 * panel says 100%; a teacher able to *change* it would be deciding who competes.
 */
function CompanyCard({
  dancerId,
  firstName,
  isCompanyMember,
  canSet,
}: {
  dancerId: string
  firstName: string
  isCompanyMember: boolean
  canSet: boolean
}) {
  const queryClient = useQueryClient()

  const set = useMutation({
    mutationFn: (next: boolean) => setCompanyMember(dancerId, next),
    /*
     * Her record and both attendance reads: the flag changes the threshold she
     * is measured against, so a panel left on the old number would contradict
     * the switch that had just been flicked (D-271).
     */
    onSuccess: () => {
      /*
       * **The keys the figures actually live under**, which the first cut got
       * wrong in two of four: `['standing']` matches nothing (the queue is
       * `['standing-alerts', …]`) and `['roster']` refreshes the list of names
       * rather than the numbers beside them. `CountsAsOneDialog` does the same
       * job for the same reason — it also changes what a dancer's arithmetic
       * is — so its keys are the precedent.
       */
      for (const key of [
        ['dancer', dancerId],
        ['dancer-panel', dancerId],
        ['dancer-standing'],
        ['class-standings'],
        ['standing-alerts'],
      ]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  // Nothing to read and nothing to set: not a section (D-415).
  if (!canSet && !isCompanyMember) return null

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">Company</h2>

      {canSet ? (
        <label className="mt-2 flex cursor-pointer items-start gap-2.5 text-[13px] text-ink-2">
          <input
            type="checkbox"
            checked={isCompanyMember}
            disabled={set.isPending}
            onChange={(e) => set.mutate(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span>
            {firstName} is on the company team
            <span className="mt-0.5 block text-[12px] text-ink-3">
              Held to a higher attendance figure, and told where she stands from her first marked
              class rather than after a few weeks.
            </span>
          </span>
        </label>
      ) : (
        <p className="mt-1 text-[13.5px] text-ink-2">
          {firstName} is on the company team.
          <span className="mt-0.5 block text-[12px] text-ink-3">
            That is why her attendance is measured against a higher figure.
          </span>
        </p>
      )}

      {set.isError && (
        <p className="mt-2 text-[12px] text-crit">
          {set.error instanceof Error ? set.error.message : 'That could not be saved.'}
        </p>
      )}
    </section>
  )
}

/**
 * Whether her privates are charged against credit on the account (D-662).
 *
 * **Owner, from the pilot:** *"Tonya needs a way to flag students who hold
 * credit on their account to pay for privates or makeups... when those parents
 * sign up, instead of being told to pay out of pocket they see it coming out of
 * their banked money."*
 *
 * **A flag, never a balance.** The app does not track the money and never will
 * — Tonya does the charging in Jackrabbit (D-54). A number here would be a
 * second ledger that has to agree with the real one, and it would be wrong the
 * first time she spent any of it.
 *
 * Its own card rather than a second tick inside Company, because the two are
 * unrelated facts about a child that happen to live on one screen: a company
 * dancer is measured differently, and this one is billed differently.
 */
function AccountCreditCard({
  dancerId,
  firstName,
  holdsAccountCredit,
  canSet,
}: {
  dancerId: string
  firstName: string
  holdsAccountCredit: boolean
  canSet: boolean
}) {
  const queryClient = useQueryClient()

  const set = useMutation({
    mutationFn: (next: boolean) => setAccountCredit(dancerId, next),
    /*
     * Her own record, which is the only screen in *this* browser that reads the
     * flag. The sentence a parent sees at booking lives in her browser and
     * refreshes on her next load - a key for it here would be decoration, which
     * is the mistake CompanyCard above records making in two of four.
     */
    onSuccess: () => {
      for (const key of [
        ['dancer', dancerId],
        ['dancer-panel', dancerId],
      ]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  // Nothing to read and nothing to set: not a section (D-415).
  if (!canSet && !holdsAccountCredit) return null

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
        Paying for privates
      </h2>

      {canSet ? (
        <label className="mt-2 flex cursor-pointer items-start gap-2.5 text-[13px] text-ink-2">
          <input
            type="checkbox"
            checked={holdsAccountCredit}
            disabled={set.isPending}
            onChange={(e) => set.mutate(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span>
            {firstName} has credit on her account
            <span className="mt-0.5 block text-[12px] text-ink-3">
              Her family is told a private comes out of that rather than that they pay the teacher.
              You still do the charging in Jackrabbit &mdash; nothing here tracks a balance.
            </span>
          </span>
        </label>
      ) : (
        <p className="mt-1 text-[13.5px] text-ink-2">
          {firstName} has credit on her account.
          <span className="mt-0.5 block text-[12px] text-ink-3">
            That is why her family is told a private comes out of it.
          </span>
        </p>
      )}

      {set.isError && (
        <p className="mt-2 text-[12px] text-crit">
          {set.error instanceof Error ? set.error.message : 'That could not be saved.'}
        </p>
      )}
    </section>
  )
}

/**
 * Giving a dancer a staff record, because she helps with a class (D-535).
 *
 * **She is one human on two lists**, and until this existed nothing said so —
 * the import made Ava Webber a dancer row and a separate staff row, with no
 * connection between them.
 *
 * **Tonya names the record; the app never matches one.** An earlier version
 * paired them on first and last name, which refused when it was certain the
 * name was ambiguous and guessed when it could not tell — so a married
 * surname or a nickname silently produced a second person. Names are for
 * display, ids are for matching (D-82).
 */
function StaffRecordCard({
  dancerId,
  firstName,
  lastName,
  alreadyIs,
}: {
  dancerId: string
  firstName: string
  lastName: string
  /** The staff record she already has, if any. Null until somebody makes one. */
  alreadyIs: string | null
}) {
  const canManage = useCan('ManageStaff')

  const [open, setOpen] = useState(false)
  const [chosen, setChosen] = useState<string | ''>('')
  const [email, setEmail] = useState('')

  const queryClient = useQueryClient()

  const { data: candidates } = useQuery({
    queryKey: ['staff-candidates', dancerId],
    queryFn: () => fetchStaffCandidates(dancerId),
    enabled: open,
  })

  const promote = useMutation({
    mutationFn: () =>
      makeDancerStaff(dancerId, chosen ? { staffId: chosen } : { email: email.trim(), mobilePhone: null }),
    onSuccess: () => {
      setOpen(false)

      /*
       * **The dancer record first, because it is the one this screen renders.**
       * Without it the section returned to "Give Ava a staff record" and
       * success looked identical to nothing having happened — which is how
       * somebody tries again, meets the endpoint's idempotent early return, and
       * believes she set an address that was never read.
       *
       * `['staff']` is the room-booking teacher picker; `['staff-accounts']`
       * is the staff screen. Both now show a person they did not before.
       */
      for (const key of [['dancer', dancerId], ['staff-accounts'], ['staff'], ['staff-candidates', dancerId]]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  // Nothing to offer somebody who cannot manage staff, and a control that
  // refuses the person holding it is worse than no control.
  if (!canManage) return null

  const ready = chosen !== '' || email.trim().length > 0

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
        Helping with classes
      </h2>

      {alreadyIs ? (
        /*
         * **The state, not a toast.** This card writes an identity, and a
         * screen that writes one must be able to read it back — otherwise
         * success and nothing-happened are the same pixels, and the second
         * attempt meets an idempotent 200 that never looks at what was typed.
         */
        <p className="mt-1 text-[13.5px]">
          On the staff list as <strong className="font-semibold">{alreadyIs}</strong>.
          <span className="mt-0.5 block text-[12.5px] text-ink-3">
            The same person, on both lists. Her dancing is unaffected.
          </span>
        </p>
      ) : !open ? (
        <>
          <p className="mt-1 text-[13px] text-ink-3">
            If {firstName} helps teach, she needs a staff record as well as this one. It
            does not give her a login.
          </p>

          <button
            type="button"
            onClick={() => setOpen(true)}
            className="mt-2.5 rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
          >
            Give {firstName} a staff record
          </button>
        </>
      ) : (
        <form
          className="mt-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (ready && !promote.isPending) promote.mutate()
          }}
        >
          <label className="text-[12.5px] font-semibold">
            Is she already on the staff list?
            <select
              value={chosen}
              onChange={(e) => setChosen(e.target.value)}
              className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
            >
              <option value="">No &mdash; make her a new one</option>
              {candidates?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.email ? ` (${c.email})` : ''}
                  {c.likelyMatch ? ' — same name' : ''}
                </option>
              ))}
            </select>
          </label>

          {chosen === '' && (
            <label className="mt-2.5 block text-[12.5px] font-semibold">
              Her email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-line bg-surface-2 px-2.5 py-2 text-[13.5px] font-normal"
              />
              <span className="mt-1 block font-normal text-[11.5px] text-ink-3">
                Needed so a Jackrabbit import recognises her rather than adding a second{' '}
                {firstName} {lastName}.
              </span>
            </label>
          )}

          {promote.error && (
            <p role="alert" className="mt-2.5 text-[13px] text-crit">
              {(promote.error as Error).message}
            </p>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={!ready || promote.isPending}
              className="rounded-lg bg-accent px-3.5 py-2 text-[13px] font-semibold text-on-accent disabled:opacity-60"
            >
              {promote.isPending ? 'Saving...' : 'Save'}
            </button>

            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded-lg border border-line px-3.5 py-2 text-[13px] font-semibold"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </section>
  )
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="page-shell px-5 py-8 text-sm text-ink-3">{children}</div>
}

const measureField =
  'w-full rounded-lg border border-line-2 bg-surface px-3 py-2 text-[13.5px] outline-none focus:border-accent'

/**
 * Costume sizes and measurements — one editable set (D-198).
 *
 * <b>One card, where there were two.</b> D-187 added a second block to keep the
 * studio's own figures away from an import that overwrote unconditionally: the
 * right problem solved the wrong way, and it put two Waists on one screen. Once
 * the import only fills gaps, nothing needs hiding from it.
 *
 * The studio owns these now — Tonya never used Jackrabbit's costume fields, she
 * kept a spreadsheet — so this is the record a costume is ordered from.
 */
function MeasurementsCard({ dancerId, record }: { dancerId: string; record: DancerRecord }) {
  /*
   * **The server decides, not the browser** (D-397).
   *
   * It was `useCan('ManagePeople')`. A teacher may now record sizes for a
   * dancer she teaches — and this record carries teacher *names*, never ids
   * (D-82), so the screen has no way to work that out. It is answered where
   * the permissions are known and reported on the record, which is the same
   * rule as a link that would refuse the person holding it.
   */
  const canEdit = record.canEditMeasurements
  const queryClient = useQueryClient()

  const saved = record.measurements
  const [form, setForm] = useState<DancerMeasurements>(saved)
  const [open, setOpen] = useState(false)

  // Re-syncs when the record reloads, so a save elsewhere does not leave this
  // holding a stale copy that would be written back on the next edit.
  useEffect(() => setForm(saved), [saved])

  const save = useMutation({
    mutationFn: () => saveMeasurements(dancerId, form),
    onSuccess: (updated) => {
      queryClient.setQueryData(['dancer', dancerId], (old: DancerRecord | undefined) =>
        old ? { ...old, measurements: updated } : old,
      )
      setOpen(false)
    },
  })

  const set = (patch: Partial<DancerMeasurements>) => setForm((f) => ({ ...f, ...patch }))
  const dirty = JSON.stringify(form) !== JSON.stringify(saved)

  const recorded = recordedMeasurements(saved)
  const age = measurementAge(saved.measuredOn)

  const body = measurementFields(form).filter((f) => f.group === 'body')
  const sizes = measurementFields(form).filter((f) => f.group === 'size')

  return (
    <section className="overflow-hidden rounded-2xl border border-line bg-surface">
      <header className="flex items-center gap-2.5 border-b border-line bg-surface-2 px-4 py-3">
        <Ruler size={15} strokeWidth={2.5} className="shrink-0 text-ink-2" />
        <div className="min-w-0 flex-1">
          <h2 className="text-[13.5px] font-bold">Costume sizes &amp; measurements</h2>
          {/*
            Said plainly, because it is the opposite of what this card used to
            say. An import fills a blank and never changes a figure somebody
            typed, so nothing here is at risk from the next upload.
          */}
          <p className="mt-0.5 text-[12px] text-ink-3">
            Kept here. An import fills gaps but never changes what you have entered.
          </p>
        </div>

        {canEdit && !open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="shrink-0 rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            {recorded.length > 0 ? 'Edit' : 'Add'}
          </button>
        )}
      </header>

      {!open && recorded.length === 0 && (
        <p className="px-4 py-4 text-[13px] text-ink-3">
          {/*
            The refusal says which refusal it is (D-397). "You cannot record
            measurements" was true of everybody who was not Tonya; now a
            teacher can, for the dancers she teaches, so the sentence has to
            distinguish "not you" from "not this dancer" or she reads it as the
            feature not existing.
          */}
          {canEdit
            ? 'Nothing measured yet.'
            : 'Nothing measured yet. Sizes are recorded by the studio, or by a teacher of one of her classes.'}
        </p>
      )}

      {!open && recorded.length > 0 && (
        <>
          {/* Only what has a value. Thirteen labelled blanks read as a fault. */}
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 px-4 py-3.5 sm:grid-cols-3">
            {recorded.map((f) => (
              <div key={f.label}>
                <dt className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
                  {f.label}
                </dt>
                <dd className="mt-0.5 text-[13.5px] wrap-anywhere tabular">{f.value}</dd>
              </div>
            ))}
          </dl>

          {age && <p className="border-t border-line px-4 py-2.5 text-[12px] text-ink-3">{age}</p>}

          {saved.notes && (
            <p className="border-t border-line px-4 py-3 text-[12.5px] wrap-anywhere text-ink-2">
              {saved.notes}
            </p>
          )}
        </>
      )}

      {open && (
        <div className="flex flex-col gap-4 px-4 py-3.5">
          <MeasureGroup
            heading="Measurements"
            fields={body}
            onChange={(key, value) => set({ [key]: value })}
          />

          <MeasureGroup
            heading="Sizes"
            fields={sizes}
            onChange={(key, value) => set({ [key]: value })}
          />

          {/*
            Free text, deliberately. The real data holds "24", "24 in" and
            "24 1/2" for one measurement — normalising would invent precision
            the tape measure did not have.
          */}
          <p className="-mt-1 text-[12px] text-ink-3">
            Write them however you take them &mdash; &ldquo;24&rdquo;, &ldquo;24 in&rdquo; and
            &ldquo;24 &frac12;&rdquo; are all kept exactly as typed.
          </p>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">Measured on</span>
            <input
              type="date"
              value={form.measuredOn ?? ''}
              onChange={(e) => set({ measuredOn: e.target.value || null })}
              className={`${measureField} sm:w-[12rem]`}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">Notes</span>
            <textarea
              value={form.notes ?? ''}
              onChange={(e) => set({ notes: e.target.value || null })}
              rows={2}
              className={measureField}
            />
          </label>

          {save.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(save.error as Error).message}
            </p>
          )}

          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!dirty || save.isPending}
              onClick={() => save.mutate()}
              className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
            >
              {save.isPending ? 'Saving...' : dirty ? 'Save' : 'Saved'}
            </button>

            <button
              type="button"
              onClick={() => {
                setForm(saved)
                setOpen(false)
              }}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

function MeasureGroup({
  heading,
  fields,
  onChange,
}: {
  heading: string
  fields: { key: MeasurementKey; label: string; value: string | null }[]
  onChange: (key: MeasurementKey, value: string | null) => void
}) {
  return (
    <div>
      <p className="mb-1.5 text-[11px] font-bold tracking-[0.08em] text-ink-3 uppercase">
        {heading}
      </p>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {fields.map((f) => (
          <label key={f.key} className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">{f.label}</span>
            <input
              value={f.value ?? ''}
              onChange={(e) => onChange(f.key, e.target.value || null)}
              className={measureField}
            />
          </label>
        ))}
      </div>
    </div>
  )
}

/**
 * Whether she is on the Mini team (D-841).
 *
 * **Modelled on `CompanyCard` above and gated by the same answer**, because it is the
 * same `ManagePeople` door — the capability that edits a dancer at all.
 *
 * ⚠ **The gate is the load-bearing half of the whole not-called feature.** A teacher
 * may declare the Mini team out of a rehearsal and must not be able to decide who is
 * on it; if she could, she could aim a declaration at one named child, which is the
 * thing D-188 took the Excused button away to prevent. So a teacher sees this and
 * cannot change it — the same split `CompanyCard` makes, for a sharper reason.
 *
 * **Shown to a teacher who cannot set it**, because it is how she understands why a
 * dancer's register row says *Not called* on a Wednesday.
 */
function MiniTeamCard({
  dancerId,
  firstName,
  teams,
  canSet,
}: {
  dancerId: string
  firstName: string
  teams: string[]
  canSet: boolean
}) {
  const queryClient = useQueryClient()
  const isMember = teams.includes(MINI_TEAM)

  const set = useMutation({
    mutationFn: (next: boolean) => setDancerTeam(dancerId, MINI_TEAM, next),
    /*
     * Her record, and the register that reads the team (D-841). Membership decides who
     * a declaration covers, so a sheet left on the old answer would offer a control
     * whose count had already moved — the disagreement D-848 exists to prevent.
     */
    onSuccess: () => {
      for (const key of [['dancer', dancerId], ['dancer-panel', dancerId], ['sheet']]) {
        queryClient.invalidateQueries({ queryKey: key })
      }
    },
  })

  // Nothing to read and nothing to set: not a section (D-415).
  if (!canSet && !isMember) return null

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <h2 className="text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
        Rehearsal call
      </h2>

      {canSet ? (
        <label className="mt-2 flex cursor-pointer items-start gap-2.5 text-[13px] text-ink-2">
          <input
            type="checkbox"
            checked={isMember}
            disabled={set.isPending}
            onChange={(e) => set.mutate(e.target.checked)}
            className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
          />
          <span>
            {firstName} is on the {MINI_TEAM}
            <span className="mt-0.5 block text-[12px] text-ink-3">
              A teacher can mark the whole {MINI_TEAM} as not called for one rehearsal, so
              those dancers are not marked absent for a night nobody asked them to.
            </span>
          </span>
        </label>
      ) : (
        <p className="mt-1 text-[13.5px] text-ink-2">
          {firstName} is on the {MINI_TEAM}.
          <span className="mt-0.5 block text-[12px] text-ink-3">
            That is why a rehearsal can show her as not called rather than absent.
          </span>
        </p>
      )}

      {set.isError && (
        <p className="mt-2 text-[12.5px] text-[var(--color-danger)]">
          {(set.error as Error).message}
        </p>
      )}
    </section>
  )
}
